import type { Schemas } from '#shopware'

export const useDynamicPrice = (
  product: MaybeRef<Schemas['Product']>,
  quantity: MaybeRef<number> = 1
) => {
  const {
    unitPrice,
    displayFromVariants,
    displayFrom,
    isListPrice: isProductListPrice,
    tierPrices,
  } = useProductPrice(product)

  // The tier price that applies to the current quantity.
  // The last tier applies to its exact quantity and above, all others up to their quantity.
  const currentTierPrice = computed(() => {
    const tierPrices = unref(product).calculatedPrices
    if (!tierPrices.length) return undefined

    return (
      tierPrices.find((tierPrice) => tierPrice.quantity >= unref(quantity)) ??
      tierPrices[tierPrices.length - 1]
    )
  })

  const b2cPrice = computed(() => {
    return product.value.extensions?.b2cPriceInfo?.b2cPrice
  })

  const price = computed(() => {
    return currentTierPrice.value?.unitPrice ?? unitPrice.value
  })

  const total = computed(() => {
    if (!price.value || Number.isNaN(price.value)) return undefined
    return +(price.value * unref(quantity)).toFixed(2)
  })

  // Whether the price for the current quantity has a strike-through price.
  // Tiers of one product can differ here (e.g. a promotion only from 50 pieces on),
  // so this must be derived from the current tier and not from the cheapest one.
  const isListPrice = computed(() => {
    if (!currentTierPrice.value) return isProductListPrice.value
    return !!currentTierPrice.value.listPrice?.percentage
  })

  const base = computed(() => {
    if (!currentTierPrice.value) {
      return unref(product).calculatedPrice?.listPrice?.price ?? unitPrice.value
    }
    return currentTierPrice.value.listPrice?.price
  })

  const baseTotal = computed(() => {
    if (!base.value || Number.isNaN(base.value)) return undefined
    return +(base.value * unref(quantity)).toFixed(2)
  })

  return {
    price,
    total,
    baseTotal,
    displayFromVariants,
    displayFrom,
    isListPrice,
    tierPrices,
    b2cPrice,
  }
}
